import React, { useEffect, useMemo, useState } from 'react';
import {
  User, Lock, Monitor, Save, Trash2, Shield, Loader2, Check,
  Bell, Globe, Sparkles, Phone, Briefcase, Building2, Mail,
  FileText, Receipt, GraduationCap, Calendar, Send, Upload, Camera,
} from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import AvatarUploader from '../components/AvatarUploader';
import TwoFactorCard from '../components/TwoFactorCard';
import EmailMailboxSection from '../components/EmailMailboxSection';
import { scorePassword } from '../utils/passwordStrength';
import { oauthRedirect } from '../utils/oauthRedirect';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from '../hooks/use-toast';
import { useWebPush } from '../hooks/useWebPush';

// ---------------------------------------------------------------------------
// Hero card — Wave 1u9 redesign: premium cockpit aesthetic + grain + glow
// ---------------------------------------------------------------------------
function ProfileHero({ user, stats, sessionsCount }) {
  const initials = (user?.name || user?.email || '?')
    .split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  const memberSince = stats?.member_since
    ? new Date(stats.member_since).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })
    : null;
  const lastLogin = stats?.last_login
    ? new Date(stats.last_login).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
    : null;
  const counts = stats?.counts || {};

  const tiles = [
    { label: 'Sessions actives', value: sessionsCount, icon: Monitor, accent: '#A5B4FC' },
    { label: 'Devis', value: counts.quotes, icon: FileText, accent: '#5EEAD4' },
    { label: 'Factures', value: counts.invoices, icon: Receipt, accent: '#FCD34D' },
    { label: 'Sessions form.', value: counts.sessions, icon: GraduationCap, accent: '#93C5FD' },
    { label: 'Apprenants', value: counts.learners, icon: User, accent: '#FCA5A5' },
    { label: 'E-mails envoyés', value: counts.emails_sent, icon: Send, accent: '#C4B5FD' },
  ];

  return (
    <div data-testid="profile-hero" className="relative rounded-2xl overflow-hidden shadow-[0_8px_40px_-12px_rgba(20,19,46,0.35)]">
      {/* Layered background */}
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(at 18% 0%, #2A2356 0%, #14132E 55%, #0A0920 100%)' }}
      />
      {/* Grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.1] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
      {/* Glow accents */}
      <div className="absolute -top-20 -right-16 w-[360px] h-[360px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(124,92,252,0.45), transparent 65%)' }} />
      <div className="absolute -bottom-24 -left-16 w-[300px] h-[300px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.3), transparent 65%)' }} />

      {/* Content */}
      <div className="relative z-10 p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="relative">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-20 h-20 rounded-2xl object-cover ring-2 ring-white/20 shadow-2xl" />
            ) : (
              <div
                className="w-20 h-20 rounded-2xl text-white font-bold text-[24px] flex items-center justify-center ring-2 ring-white/20 shadow-2xl"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  backdropFilter: 'blur(8px)',
                }}
              >
                {initials}
              </div>
            )}
            {/* Online dot */}
            <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-400 ring-2 ring-[#14132E]" title="En ligne" />
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-[24px] sm:text-[28px] font-bold text-white tracking-tight leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="profile-hero-name">
              {user?.name || user?.email}
            </h2>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.08] backdrop-blur text-white text-[11.5px] font-semibold border border-white/10">
                <Shield className="w-3 h-3" /> {user?.role || 'Utilisateur'}
              </span>
              {(user?.org_name || user?.org) && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.08] backdrop-blur text-white text-[11.5px] font-semibold border border-white/10">
                  <Building2 className="w-3 h-3" /> {user.org_name || user.org}
                </span>
              )}
              {memberSince && (
                <span className="inline-flex items-center gap-1 text-[11.5px] text-white/55">
                  <Calendar className="w-3 h-3" /> Membre depuis {memberSince}
                </span>
              )}
            </div>
            <div className="text-[12.5px] text-white/65 mt-2 flex items-center gap-1.5 flex-wrap">
              <Mail className="w-3 h-3" /> {user?.email}
              {lastLogin && <><span className="text-white/30">·</span> Dernière connexion : {lastLogin}</>}
            </div>
          </div>
        </div>

        {/* Stats grid — translucent tiles aligned with cockpit theme */}
        <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {tiles.map((t) => (
            <div
              key={t.label}
              className="rounded-xl px-3.5 py-3 bg-white/[0.06] backdrop-blur border border-white/10 hover:bg-white/[0.09] transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${t.accent}20`, color: t.accent }}>
                  <t.icon className="w-3.5 h-3.5" />
                </div>
                <div className="text-[20px] font-bold text-white leading-none tabular-nums" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {t.value || 0}
                </div>
              </div>
              <div className="text-[10.5px] text-white/55 uppercase tracking-wider mt-2 font-semibold">{t.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — Informations personnelles (extended fields)
// ---------------------------------------------------------------------------
function InfoTab({ user, onUpdated }) {
  const [form, setForm] = useState({
    name: user?.name || '',
    job_title: user?.job_title || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put('/me/profile', form);
      onUpdated(data);
      toast({ title: 'Profil mis à jour' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Sauvegarde impossible' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="soft-card p-6 space-y-4" data-testid="info-tab">
      <div className="pb-4 border-b border-[#F3F4F8]">
        <AvatarUploader user={user} onUpdated={onUpdated} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Nom complet" icon={User}>
          <input data-testid="profile-name" value={form.name} onChange={set('name')} maxLength={80} className={INPUT_CLS} />
        </Field>
        <Field label="Fonction" icon={Briefcase}>
          <input data-testid="profile-job_title" value={form.job_title} onChange={set('job_title')} maxLength={120} placeholder="Responsable formation" className={INPUT_CLS} />
        </Field>
        <Field label="Téléphone" icon={Phone}>
          <input data-testid="profile-phone" value={form.phone} onChange={set('phone')} maxLength={30} placeholder="+33 6 12 34 56 78" className={INPUT_CLS} type="tel" />
        </Field>
      </div>
      <Field label="Bio courte" icon={Sparkles} hint="Visible par les autres membres de votre organisation. 400 caractères max.">
        <textarea
          data-testid="profile-bio"
          value={form.bio}
          onChange={set('bio')}
          maxLength={400}
          rows={3}
          placeholder="Parlez-nous de vous, de votre expertise, ou de votre rôle…"
          className={`${INPUT_CLS} h-auto py-2.5 resize-none`}
        />
        <div className="text-[11px] text-[#8E8FA5] text-right mt-0.5">{form.bio.length} / 400</div>
      </Field>
      <div className="flex justify-end pt-2">
        <button data-testid="profile-save" type="submit" disabled={saving} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-1.5 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Sécurité (password + sessions)
// ---------------------------------------------------------------------------
function SecurityTab({ user }) {
  // Vague 112 — 3-column security grid. Each card surfaces a badge: active
  // method ("Connecté via"), configured vs not, plus its dedicated action.
  const lastMethod = user?.last_login_method; // 'email' | 'google' | 'magic' | undefined
  const hasPassword = Boolean(user?.has_password);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch" data-testid="security-methods-grid">
        <div className="soft-card p-5 flex flex-col h-full">
          <MethodCardHeader
            icon={Lock}
            title="Mot de passe"
            configured={hasPassword}
            active={lastMethod === 'email'}
            testid="method-password"
          />
          <p className="text-[12px] text-[#6B6C85] mb-3">
            {hasPassword
              ? <>Changez-le régulièrement. Les autres appareils seront déconnectés à chaque modification.</>
              : <>Aucun mot de passe — définissez-en un pour activer la connexion email.</>}
          </p>
          <div className="mt-auto">
            <PasswordForm hasPassword={hasPassword} mustChange={Boolean(user?.must_change_password)} />
          </div>
        </div>
        <TwoFactorCard lastMethod={lastMethod} />
        <GoogleAccountLinkCard user={user} lastMethod={lastMethod} />
        <MicrosoftAccountLinkCard user={user} lastMethod={lastMethod} />
      </div>
      <div className="soft-card p-6">
        <SectionHeader icon={Monitor} title="Appareils connectés" description="Liste de vos sessions actives. Révoquez toute session suspecte." />
        <SessionsList />
      </div>
    </div>
  );
}

/** Header shared by the 3 security method cards — icon + title + 2 badges. */
function MethodCardHeader({ icon: Icon, title, configured, active, testid }) {
  return (
    <div className="flex items-start justify-between gap-2 mb-3">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${configured ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'bg-[#F3F4F8] text-[#8E8FA5]'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[14px] font-bold text-[#1A1B2E] leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className={`text-[10.5px] font-bold px-1.5 py-0.5 rounded ${configured ? 'bg-[#DCFCE7] text-[#15803D]' : 'bg-[#F3F4F8] text-[#6B6C85]'}`} data-testid={`${testid}-configured`}>
              {configured ? 'Activé' : 'Non configuré'}
            </span>
            {active && (
              <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5] inline-flex items-center gap-1" title="Méthode utilisée à votre dernière connexion" data-testid={`${testid}-active`}>
                <Check className="w-3 h-3" /> Connecté via
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vague 106 — Google account linking (opt-in per user)
// ---------------------------------------------------------------------------
function GoogleAccountLinkCard({ user, lastMethod }) {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const reload = async () => {
    try {
      const [cfg, st] = await Promise.all([
        api.get('/integrations/google/config'),
        api.get('/integrations/google/status'),
      ]);
      setConfig(cfg.data);
      setStatus(st.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const linkGoogle = (kind /* 'basic' | 'gmail' */) => {
    if (!config?.configured || !config?.client_id) return;
    setWorking(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
    const redirectUri = `${window.location.origin}/auth/google`;
    const state = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    sessionStorage.setItem('google_oauth_state', state);
    sessionStorage.setItem('google_oauth_after', '/app/profil');
    // Vague 112 — flag the callback as a "link" intent so /auth/google attaches
    // the google_sub to the CURRENT user instead of switching session to any
    // user that happens to match the Google email.
    sessionStorage.setItem('google_oauth_link_mode', '1');
    const scopes = kind === 'gmail'
      ? (config.scopes_gmail || ['openid','email','profile',
          'https://www.googleapis.com/auth/gmail.readonly',
          'https://www.googleapis.com/auth/gmail.send',
          'https://www.googleapis.com/auth/calendar'])
      : (config.scopes_sso || ['openid','email','profile']);
    const params = new URLSearchParams({
      client_id: config.client_id,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: scopes.join(' '),
      prompt: 'consent',
      state,
      include_granted_scopes: 'true',
    });
    if (kind === 'gmail') params.set('access_type', 'offline');
    const url = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    oauthRedirect(url);
  };

  const unlink = async () => {
    if (!window.confirm("Délier votre compte Google ? Vous pourrez toujours vous connecter avec votre mot de passe.")) return;
    try {
      await api.post('/integrations/google/disconnect');
      toast({ title: 'Compte Google délié' });
      reload();
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' }); }
  };

  if (loading) return null;
  if (!config?.configured) return null;
  const linked = status?.connected;
  const hasGmailScopes = linked && (status.scopes || '').includes('gmail.readonly');

  return (
    <div className="soft-card p-5 flex flex-col h-full" data-testid="google-link-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${linked ? 'bg-[#DCFCE7] text-[#15803D]' : 'bg-[#EEF2FF] text-[#4F46E5]'}`}>
            <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#4285F4" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.4 2.2 30 0 24 0 14.6 0 6.5 5.4 2.5 13.2l7.8 6.1C12.2 13.2 17.6 9.5 24 9.5z" />
              <path fill="#34A853" d="M46.1 24.6c0-1.6-.1-3.2-.4-4.6H24v9.1h12.4c-.6 2.9-2.2 5.3-4.7 6.9l7.3 5.7c4.3-4 6.8-9.8 6.8-17.1z" />
              <path fill="#FBBC05" d="M10.3 28.7c-.5-1.4-.8-2.9-.8-4.7s.3-3.3.8-4.7l-7.8-6.1C.9 16.4 0 20.1 0 24s.9 7.6 2.5 10.8l7.8-6.1z" />
              <path fill="#EA4335" d="M24 48c6 0 11.3-2 15.1-5.4l-7.3-5.7c-2.1 1.4-4.8 2.2-6.9 2.2-5.3 0-9.8-3.4-11.3-8.1l-6.5 5C9.5 39.5 16.2 44 24 44z" />
            </svg>
          </div>
          <div>
            <div className="text-[14px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Connexion Google</div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className={`text-[10.5px] font-bold px-1.5 py-0.5 rounded ${linked ? 'bg-[#DCFCE7] text-[#15803D]' : 'bg-[#F3F4F8] text-[#6B6C85]'}`} data-testid="method-google-configured">
                {linked ? 'Lié' : 'Non configuré'}
              </span>
              {lastMethod === 'google' && (
                <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5] inline-flex items-center gap-1" title="Méthode utilisée à votre dernière connexion" data-testid="method-google-active">
                  <Check className="w-3 h-3" /> Connecté via
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <p className="text-[12px] text-[#6B6C85] mb-3">
        {linked ? (hasGmailScopes ? 'Connexion + Gmail + Agenda activés.' : 'Connexion en un clic activée.') : 'Liez votre compte Google pour vous connecter en un clic. Gmail + Agenda en option.'}
      </p>

      {linked ? (
        <div className="space-y-3 mt-auto">
          <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-[#E6FBF7] border border-[#A7F3D0]">
            <div className="min-w-0">
              <div className="text-[12.5px] font-semibold text-[#0F766E] truncate">{status.email || 'Compte Google lié'}</div>
              <p className="text-[11px] text-[#0F766E]/80 truncate">
                {hasGmailScopes ? 'Gmail + Agenda' : 'Connexion en un clic'}
              </p>
            </div>
            <button
              onClick={unlink}
              data-testid="google-unlink-btn"
              className="h-9 px-3 rounded-lg border border-[#FCA5A5] text-[#B91C1C] bg-white text-[12.5px] font-semibold hover:bg-[#FEF2F2] transition-colors"
            >
              Délier
            </button>
          </div>

          {!hasGmailScopes && (
            <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-4">
              <div className="text-[12.5px] font-semibold text-[#1A1B2E] mb-1">Activer Gmail + Google Agenda</div>
              <p className="text-[11.5px] text-[#6B6C85] leading-relaxed mb-3">
                Synchronisez vos emails dans la timeline CRM et vos sessions dans Google Agenda. <br/>
                <span className="text-[#B45309] font-medium">⚠️ Cette étape demande à votre administrateur Google d'ajouter votre email dans la liste des "Test users" tant que l'app OFManager n'est pas vérifiée par Google.</span>
              </p>
              <button
                onClick={() => linkGoogle('gmail')}
                disabled={working}
                data-testid="google-activate-gmail-btn"
                className="h-9 px-3 rounded-lg bg-[#4F46E5] text-white text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#4338CA] transition-colors disabled:opacity-60"
              >
                Activer Gmail + Agenda
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => linkGoogle('basic')}
          disabled={working}
          data-testid="google-link-btn"
          className="mt-auto h-10 px-4 rounded-xl bg-white border border-[#E5E7EB] text-[#1A1B2E] text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-[#FAFAFE] transition-colors disabled:opacity-60 w-full"
        >
          <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.7 6 29 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-3.9z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.8 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.7 6 29 4 24 4 16.3 4 9.7 8.4 6.3 14.7z" />
            <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5.1l-6-4.9c-2 1.4-4.4 2.2-6.9 2.2-5.3 0-9.8-3.4-11.3-8.1l-6.5 5C9.5 39.5 16.2 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.1 4-3.9 5.3l6 4.9c4.2-3.9 6.6-9.6 6.6-16.3 0-1.3-.1-2.6-.4-3.8z" />
          </svg>
          Lier mon compte Google
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vague 113 Wave 1k — Microsoft Entra ID (Azure AD) account linking
// ---------------------------------------------------------------------------
function MicrosoftAccountLinkCard({ user, lastMethod }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get('/auth/microsoft/status');
      setStatus(data);
    } catch {
      setStatus({ configured: false, linked: false });
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const linkMicrosoft = async () => {
    if (!status?.configured) return;
    setWorking(true);
    try {
      const returnOrigin = window.location.origin;
      const { data } = await api.get(`/auth/microsoft/start?return_origin=${encodeURIComponent(returnOrigin)}`);
      if (!data?.auth_url) {
        toast({ title: 'Microsoft SSO non configuré.' });
        setWorking(false);
        return;
      }
      sessionStorage.setItem('ms_oauth_state', data.state || '');
      sessionStorage.setItem('ms_oauth_link_mode', '1');
      sessionStorage.setItem('ms_oauth_after', '/app/profil?tab=security');
      oauthRedirect(data.auth_url, { onAfterPopup: () => setWorking(false) });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Démarrage Microsoft SSO impossible' });
      setWorking(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm("Délier votre compte Microsoft ? Vous pourrez toujours vous connecter avec votre mot de passe.")) return;
    try {
      await api.post('/auth/microsoft/unlink');
      toast({ title: 'Compte Microsoft délié' });
      reload();
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' }); }
  };

  if (loading) return null;
  if (!status?.configured) return null;
  const linked = !!status?.linked;

  return (
    <div className="soft-card p-5 flex flex-col h-full" data-testid="microsoft-link-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${linked ? 'bg-[#DCFCE7] text-[#15803D]' : 'bg-[#EEF2FF] text-[#4F46E5]'}`}>
            <svg className="w-5 h-5" viewBox="0 0 23 23" aria-hidden="true">
              <path fill="#F25022" d="M1 1h10v10H1z" />
              <path fill="#7FBA00" d="M12 1h10v10H12z" />
              <path fill="#00A4EF" d="M1 12h10v10H1z" />
              <path fill="#FFB900" d="M12 12h10v10H12z" />
            </svg>
          </div>
          <div>
            <div className="text-[14px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Connexion Microsoft</div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span
                className={`text-[10.5px] font-bold px-1.5 py-0.5 rounded ${linked ? 'bg-[#DCFCE7] text-[#15803D]' : 'bg-[#F3F4F8] text-[#6B6C85]'}`}
                data-testid="method-microsoft-configured"
              >
                {linked ? 'Lié' : 'Non lié'}
              </span>
              {lastMethod === 'microsoft' && (
                <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5] flex items-center gap-1">
                  <Check className="w-3 h-3" /> Connecté via
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <p className="text-[12px] text-[#6B6C85] mb-3 leading-relaxed">
        {linked
          ? <>Compte Microsoft <strong>{status.email}</strong> — connexion 1-clic activée. Pour la synchronisation Outlook/Calendar, voir <em>Profil → Boîte email</em>.</>
          : <>Liez votre compte Microsoft Entra ID (personnel Outlook.com ou compte 365 pro) pour vous connecter en un clic. Aucun accès Mail/Calendar n'est demandé.</>}
      </p>
      <div className="mt-auto flex flex-col gap-2">
        {linked ? (
          <button
            onClick={unlink}
            data-testid="ms-link-unlink-btn"
            className="h-9 px-3 rounded-lg text-[#B91C1C] border border-[#FCA5A5] text-[12.5px] font-semibold hover:bg-[#FEF2F2]"
          >
            Délier
          </button>
        ) : (
          <button
            onClick={linkMicrosoft}
            disabled={working}
            data-testid="ms-link-connect-btn"
            className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
          >
            {working ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Lier mon compte Microsoft
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — Préférences (locale, timezone)
// ---------------------------------------------------------------------------
const LOCALE_OPTIONS = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
];
const TZ_OPTIONS = [
  'Europe/Paris', 'Europe/London', 'Europe/Madrid', 'Europe/Berlin',
  'America/New_York', 'America/Los_Angeles', 'Asia/Tokyo', 'Australia/Sydney', 'UTC',
];

function PreferencesTab({ user, onUpdated }) {
  const [locale, setLocale] = useState(user?.locale || 'fr');
  const [timezone, setTimezone] = useState(user?.timezone || 'Europe/Paris');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/me/profile', { locale, timezone });
      onUpdated(data);
      toast({ title: 'Préférences enregistrées' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Sauvegarde impossible' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="soft-card p-6 space-y-4" data-testid="prefs-tab">
      <SectionHeader icon={Globe} title="Préférences régionales" description="Langue d'affichage et fuseau horaire pour les dates et notifications." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Langue" icon={Globe}>
          <select data-testid="prefs-locale" value={locale} onChange={(e) => setLocale(e.target.value)} className={INPUT_CLS}>
            {LOCALE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Fuseau horaire" icon={Globe}>
          <select data-testid="prefs-timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} className={INPUT_CLS}>
            {TZ_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
      </div>
      <div className="flex justify-end pt-2">
        <button data-testid="prefs-save" onClick={save} disabled={saving} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-1.5 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 4 — Notifications (toggles)
// ---------------------------------------------------------------------------
const NOTIF_DEFS = [
  { key: 'quote_signed', label: 'Devis signés', description: 'Vous êtes prévenu lorsqu\'un client signe un devis.' },
  { key: 'invoice_paid', label: 'Factures payées', description: 'Notification lors de la réception d\'un paiement.' },
  { key: 'invoice_overdue', label: 'Factures en retard', description: 'Alerte hebdomadaire des factures non payées.' },
  { key: 'session_reminder', label: 'Sessions à venir', description: 'Rappel J-3 et J-1 avant le démarrage d\'une session.' },
  { key: 'weekly_digest', label: 'Récap hebdomadaire', description: 'Le lundi matin, un résumé de votre semaine et de votre pipeline.' },
  { key: 'product_news', label: 'Actualités produit', description: 'Nouvelles fonctionnalités, conseils d\'utilisation. Très occasionnel.' },
];

function NotificationsTab({ user, onUpdated }) {
  const initial = useMemo(() => {
    const prefs = user?.notification_prefs || {};
    return Object.fromEntries(NOTIF_DEFS.map((n) => [n.key, prefs[n.key] !== false])); // default ON
  }, [user]);
  const [prefs, setPrefs] = useState(initial);
  const [saving, setSaving] = useState(false);

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/me/profile', { notification_prefs: prefs });
      onUpdated(data);
      toast({ title: 'Notifications enregistrées' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Sauvegarde impossible' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="soft-card p-6" data-testid="notif-tab">
      <SectionHeader icon={Bell} title="Notifications email" description="Choisissez les évènements pour lesquels vous souhaitez recevoir un email." />
      <div className="space-y-2">
        {NOTIF_DEFS.map((n) => (
          <label
            key={n.key}
            data-testid={`notif-${n.key}`}
            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${prefs[n.key] ? 'border-[#C7D2FE] bg-[#FAF6FF]' : 'border-[#EFF0F6] bg-white hover:border-[#C7D2FE]'}`}
          >
            <Toggle checked={!!prefs[n.key]} onChange={() => toggle(n.key)} />
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-semibold text-[#1A1B2E]">{n.label}</div>
              <div className="text-[12px] text-[#6B6C85] leading-snug">{n.description}</div>
            </div>
          </label>
        ))}
      </div>
      <div className="flex justify-end pt-4">
        <button data-testid="notif-save" onClick={save} disabled={saving} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-1.5 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
      <WebPushPanel />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Web Push panel — Vague 99.5
// ---------------------------------------------------------------------------
function WebPushPanel() {
  const { supported, status, loading, subscribe, unsubscribe, sendTest } = useWebPush();

  const isOn = status === 'subscribed';
  const isDenied = status === 'denied';

  return (
    <div className="mt-6 pt-6 border-t border-[#EFF0F6]" data-testid="webpush-panel">
      <SectionHeader
        icon={Bell}
        title="Notifications navigateur (Push)"
        description="Recevez une alerte temps réel sur votre poste — même quand OFManager est fermé — pour les paiements Stripe et les évènements critiques."
      />
      {!supported ? (
        <div className="text-[12.5px] text-[#92400E] bg-[#FFF7E6] border border-[#FDE68A] rounded-xl p-3">
          Votre navigateur ne supporte pas les notifications Push (essayez Chrome, Edge, Firefox ou Safari récents).
        </div>
      ) : isDenied ? (
        <div className="text-[12.5px] text-[#B91C1C] bg-[#FEE2E2] border border-[#FECACA] rounded-xl p-3">
          Les notifications sont bloquées dans votre navigateur. Activez-les via le cadenas dans la barre d'adresse.
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {isOn ? (
            <>
              <button onClick={unsubscribe} disabled={loading} data-testid="webpush-unsubscribe"
                      className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[#4A4B63] text-[13px] font-semibold disabled:opacity-60">
                Désactiver
              </button>
              <button onClick={async () => {
                const sent = await sendTest();
                toast({ title: sent ? `${sent} notification(s) envoyée(s)` : 'Aucun terminal abonné', description: sent ? 'Vous devriez voir une notification.' : '' });
              }} data-testid="webpush-test"
                      className="h-10 px-4 rounded-xl bg-[#EEF2FF] text-[#4F46E5] text-[13px] font-semibold">
                Tester maintenant
              </button>
              <span className="text-[12px] text-[#0F766E] bg-[#E6FBF7] px-2.5 py-1 rounded-md font-semibold">Actives sur ce navigateur</span>
            </>
          ) : (
            <button onClick={subscribe} disabled={loading} data-testid="webpush-subscribe"
                    className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
              Activer les notifications Push
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const INPUT_CLS = "w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors";

function Field({ label, icon: Icon, hint, children }) {
  return (
    <div>
      <label className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] mb-1.5 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </label>
      {children}
      {hint && <div className="text-[11px] text-[#8E8FA5] mt-1">{hint}</div>}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h3 className="text-[16px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</h3>
        {description && <p className="text-[13px] text-[#8E8FA5] mt-0.5">{description}</p>}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); onChange(); }}
      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 mt-0.5 ${checked ? 'bg-[#4F46E5]' : 'bg-[#D4D5E0]'}`}
      role="switch"
      aria-checked={checked}
    >
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${checked ? 'left-[22px]' : 'left-0.5'}`} />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Password form (legacy — kept as-is, just inlined here)
// ---------------------------------------------------------------------------
function PasswordForm({ hasPassword, mustChange = false }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (next.length < 8) { setError('Min. 8 caractères'); return; }
    if (next !== confirm) { setError('Les deux mots de passe ne correspondent pas'); return; }
    const { common, score } = scorePassword(next);
    if (common) { setError('Ce mot de passe est trop courant.'); return; }
    if (score < 2) { setError('Mot de passe trop faible'); return; }
    setSaving(true);
    try {
      await api.post('/me/change-password', {
        current_password: hasPassword && !mustChange ? current : undefined,
        new_password: next,
      });
      setCurrent(''); setNext(''); setConfirm(''); setSuccess(true);
      toast({ title: hasPassword ? 'Mot de passe mis à jour' : 'Mot de passe défini' });
      setTimeout(() => setSuccess(false), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Changement impossible');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {!hasPassword && (
        <div className="text-[12px] text-[#0369A1] bg-[#E0F2FE] border border-[#7DD3FC] rounded-lg px-3 py-2">
          Aucun mot de passe n'est défini (connexion Google uniquement). Définissez-en un pour activer la connexion email.
        </div>
      )}
      {mustChange && hasPassword && (
        <div className="text-[12px] text-[#92400E] bg-[#FEF3C7] border border-[#FCD34D] rounded-lg px-3 py-2" data-testid="pw-must-change-banner">
          🔒 Votre administrateur vous a fourni un mot de passe temporaire. Définissez maintenant un mot de passe personnel — vous n'avez pas besoin de ressaisir le mot de passe temporaire.
        </div>
      )}
      {hasPassword && !mustChange && (
        <PwInput data-testid="pw-current" placeholder="Mot de passe actuel" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" />
      )}
      <PwInput data-testid="pw-new" placeholder="Nouveau mot de passe" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} autoComplete="new-password" />
      <PasswordStrengthMeter value={next} />
      <PwInput data-testid="pw-confirm" placeholder="Confirmer le nouveau mot de passe" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} autoComplete="new-password" />
      {error && <div data-testid="pw-error" className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>}
      {success && <div data-testid="pw-success" className="text-[12px] text-[#0F766E] bg-[#E6FBF7] border border-[#A7F3D0] rounded-lg px-3 py-2 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Mot de passe mis à jour. Les autres appareils ont été déconnectés.</div>}
      <button data-testid="pw-submit" type="submit" disabled={saving}
              className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60 w-full">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
        {hasPassword ? 'Changer le mot de passe' : 'Définir un mot de passe'}
      </button>
    </form>
  );
}

function PwInput(props) {
  return (
    <div className="relative">
      <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
      <input {...props} type="password" className="w-full h-10 pl-10 pr-4 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sessions list (legacy)
// ---------------------------------------------------------------------------
function SessionsList() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmRevokeOthers, setConfirmRevokeOthers] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/me/sessions');
      setSessions(data.items || []);
    } catch { setSessions([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchSessions(); }, []);

  const revokeOne = async () => {
    if (!confirmRevoke) return;
    try {
      await api.delete(`/me/sessions/${confirmRevoke.id}`);
      setSessions((s) => s.filter((x) => x.id !== confirmRevoke.id));
      toast({ title: 'Session révoquée' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Révocation impossible' });
    } finally { setConfirmRevoke(null); }
  };

  const revokeOthers = async () => {
    try {
      const { data } = await api.post('/me/sessions/revoke-others');
      setSessions((s) => s.filter((x) => x.is_current));
      toast({ title: 'Appareils déconnectés', description: `${data.revoked} session(s) révoquée(s)` });
    } catch { toast({ title: 'Erreur' }); }
    finally { setConfirmRevokeOthers(false); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
  const otherCount = sessions.filter((s) => !s.is_current).length;

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12.5px] text-[#6B6C85]" data-testid="sessions-count">
          <span className="font-semibold text-[#1A1B2E]">{sessions.length}</span> session{sessions.length > 1 ? 's' : ''} active{sessions.length > 1 ? 's' : ''}
        </div>
        {otherCount > 0 && (
          <button data-testid="revoke-others-btn" onClick={() => setConfirmRevokeOthers(true)} className="text-[12px] font-semibold text-[#EF4444] hover:underline">
            Déconnecter les autres appareils ({otherCount})
          </button>
        )}
      </div>
      <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1" data-testid="sessions-list">
        {loading && <div className="text-[12.5px] text-[#8E8FA5] italic">Chargement…</div>}
        {!loading && sessions.length === 0 && <div className="text-[12.5px] text-[#8E8FA5] italic">Aucune session.</div>}
        {sessions.map((s) => (
          <div key={s.id} data-testid={`session-${s.id}`} className={`flex items-center gap-3 p-3 rounded-xl border ${s.is_current ? 'border-[#A7F3D0] bg-[#E6FBF7]' : 'border-[#EFF0F6] bg-white'} hover:border-[#C7D2FE] transition-colors`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${s.is_current ? 'bg-white text-[#0F766E]' : 'bg-[#F3F4F8] text-[#6B6C85]'}`}>
              <Monitor className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{s.browser} sur {s.os}</div>
                {s.is_current && <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-white text-[#0F766E] border border-[#A7F3D0]">Cet appareil</span>}
              </div>
              <div className="text-[11.5px] text-[#8E8FA5]">
                {s.ip && <>IP {s.ip} · </>}Connecté le {fmtDate(s.created_at)}
                {s.expires_at && <> · Expire {fmtDate(s.expires_at)}</>}
              </div>
            </div>
            {!s.is_current && (
              <button data-testid={`revoke-session-${s.id}`} onClick={() => setConfirmRevoke(s)} title="Révoquer cette session" className="w-8 h-8 rounded-lg text-[#EF4444] hover:bg-[#FEF2F2] flex items-center justify-center transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      <ConfirmDialog open={!!confirmRevoke} onOpenChange={(o) => !o && setConfirmRevoke(null)} title="Révoquer cette session ?" description={confirmRevoke ? `L'appareil « ${confirmRevoke.browser} sur ${confirmRevoke.os} » sera immédiatement déconnecté.` : ''} confirmLabel="Révoquer" onConfirm={revokeOne} />
      <ConfirmDialog open={confirmRevokeOthers} onOpenChange={setConfirmRevokeOthers} title="Déconnecter les autres appareils ?" description={`${otherCount} session(s) autres que cet appareil seront révoquées. Votre session courante reste active.`} confirmLabel="Déconnecter" onConfirm={revokeOthers} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page — Hero + Tabs
// ---------------------------------------------------------------------------
const TABS = [
  { id: 'info', label: 'Informations', icon: User },
  { id: 'security', label: 'Sécurité', icon: Lock },
  { id: 'mailbox', label: 'Boîte email', icon: Mail },
  { id: 'preferences', label: 'Préférences', icon: Globe },
  { id: 'notifications', label: 'Notifications', icon: Bell },
];

export default function Profil() {
  const { user, setUser } = useAuth();
  const initialTab = (() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      const allowed = new Set(['info', 'security', 'mailbox', 'preferences', 'notifications']);
      return allowed.has(t) ? t : 'info';
    } catch { return 'info'; }
  })();
  const [tab, setTab] = useState(initialTab);
  const [stats, setStats] = useState(null);
  const [sessionsCount, setSessionsCount] = useState(0);

  useEffect(() => {
    api.get('/me/stats').then(({ data }) => setStats(data)).catch(() => setStats(null));
    api.get('/me/sessions').then(({ data }) => setSessionsCount((data?.items || []).length)).catch(() => setSessionsCount(0));
  }, []);

  if (!user) return null;

  return (
    <div className="max-w-[1180px] mx-auto space-y-6" data-testid="profil-page">
      <ProfileHero user={user} stats={stats} sessionsCount={sessionsCount} />

      <div className="sticky top-0 z-10 -mx-2 px-2 pt-1">
        <div className="flex bg-white/95 backdrop-blur border border-[#EFF0F6] rounded-2xl p-1.5 w-fit shadow-[0_4px_20px_-8px_rgba(20,19,46,0.12)] overflow-x-auto" data-testid="profil-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`profil-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`h-9 px-4 rounded-xl text-[12.5px] font-semibold flex items-center gap-1.5 whitespace-nowrap transition-all ${tab === t.id ? 'bg-[#4F46E5] text-white shadow-md shadow-[#4F46E5]/25' : 'text-[#6B6C85] hover:bg-[#F6F7FB] hover:text-[#1A1B2E]'}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-5">
        {tab === 'info' && <InfoTab user={user} onUpdated={setUser} />}
        {tab === 'security' && <SecurityTab user={user} />}
        {tab === 'mailbox' && <EmailMailboxSection currentUser={user} restrictTo="user" />}
        {tab === 'preferences' && <PreferencesTab user={user} onUpdated={setUser} />}
        {tab === 'notifications' && <NotificationsTab user={user} onUpdated={setUser} />}
      </div>
    </div>
  );
}
